import type { DiscordPost } from '../net/protocol';

/**
 * A best lap's message in the Discord server it was raced in (worker/discordPosts.ts draws its picture and posts
 * it). The lap's save tells the server (it carries `post()`), so nothing big ever has to leave the game; the post
 * itself waits until the player leaves: closing the game says so on the way out, and a game put away (a phone back
 * to the chat; closing it on a phone, which goes without a word) is posted unless it's back a few seconds later.
 * The server's timer posts what's left once it's been quiet a few minutes. A session that doesn't beat the day's
 * best sends nothing. In a server without the game's bot, the posts are Discord's replies to opening the game, good
 * for 15 minutes after it opened: a best still waiting as that runs out goes out then (closing), while it can.
 */

const POSTS_KEY = 'gp.discordPosts';
/** After this long open, a best still waiting goes out (in a server without the bot; the server ignores it elsewhere). */
const CLOSING_MS = 12 * 60 * 1000;

/** The player's switch on the leaderboard: their best laps post in Discord servers (on unless turned off). */
export function postsOn(): boolean {
  try {
    return localStorage.getItem(POSTS_KEY) !== 'off';
  } catch {
    return true;
  }
}

interface Where {
  player: string;
  guild: string;
  /** The channel the game was opened in (null: Discord didn't say; the server's own channel then). */
  channel: string | null;
}

export class LapPoster {
  /** A best saved this session waits on the server: leaving says so. */
  private waiting = false;
  private leaving = false;
  /** Put away with a best waiting: coming back says so (or it's posted as left). */
  private away = false;
  private on = postsOn();

  constructor(
    private where: () => Where,
    /** The player's own link ("Race this lap" opens it), made the first time it's asked for. */
    private link: () => string,
    private base = 'api/',
  ) {
    // Closing the game (the page goes: Discord closed the Activity) posts at once. pagehide comes first.
    window.addEventListener('pagehide', () => this.leave());
    // Back from the browser's page cache after all: still here.
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) this.leaving = false;
    });
    // Put away (a phone back to the chat, or the game closed on a phone): posted unless it's back in a moment.
    document.addEventListener('visibilitychange', () => {
      if (this.leaving || !this.on) return;
      if (document.hidden) this.putAway();
      else if (this.away) this.back();
    });
    // The token from opening the game nearly spent: what's waiting goes out now.
    window.setTimeout(() => {
      if (this.waiting && this.on && !this.leaving) this.signal({ closing: '1' });
    }, CLOSING_MS);
  }

  get enabled(): boolean {
    return this.on;
  }

  /** The switch: off stops what's waiting too (on the server). */
  setEnabled(on: boolean): void {
    this.on = on;
    try {
      localStorage.setItem(POSTS_KEY, on ? 'on' : 'off');
    } catch {
      // Kept for this visit only.
    }
    if (on || !this.waiting) return;
    this.waiting = false;
    this.signal({ cancel: '1' });
  }

  /** What a lap's save carries for its post in this server (undefined: posts are off). */
  post(): DiscordPost | undefined {
    if (!this.on) return undefined;
    const w = this.where();
    return { guild: w.guild, ...(w.channel ? { channel: w.channel } : {}), link: this.link() };
  }

  /** A best saved with `post()`: it waits on the server for the player to leave. */
  saved(): void {
    if (this.on) this.waiting = true;
  }

  /** A word to the server: a few hundred bytes at most, so it gets out even as a phone closes the game. */
  private signal(extra: Record<string, string>, keepalive = false): void {
    const w = this.where();
    const q = new URLSearchParams({ player: w.player, guild: w.guild, ...extra });
    void fetch(`${this.base}discord/lap?${q}`, { method: 'POST', keepalive }).catch(() => undefined);
  }

  /** Put away: what's waiting is posted unless the game comes back first. */
  private putAway(): void {
    if (!this.waiting) return;
    this.away = true;
    this.signal({ away: '1' }, true);
  }

  /** Back from being put away (in time, or the post went out and a better lap replaces it as ever). */
  private back(): void {
    this.away = false;
    this.signal({});
  }

  private leave(): void {
    if (this.leaving || !this.on) return;
    this.leaving = true;
    if (this.waiting) this.signal({ left: '1' }, true);
  }
}
